import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntryFeedback } from '../conversationSlice';

interface LandmineAlert {
  trigger: string;
  matched_text: string;
  matched_phrase: string;  // The phrase that semantically matched
  similarity_score: number;  // Confidence 0.0-1.0 (e.g., 0.85 = 85% match)
  semantic_match: boolean;  // True for vector match, false for exact
  landmine_type: 'legal_risk' | 'detractor';  // Type of landmine
  category: string;
  priority: 'high' | 'medium' | 'low';
  can_say: Array<{
    point: string;
    context?: string;
  }>;
  cannot_say: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  quick_tips?: string;
  detected_at: string;
  timestamp?: string;
  speaker: string;
  feedback?: EntryFeedback;
}

interface LandmineState {
  alerts: LandmineAlert[]; // Current alerts for display
  allAlerts: LandmineAlert[]; // All accumulated alerts for session saving
  isConnected: boolean;
  connectionError: string | null;
}

const initialState: LandmineState = {
  alerts: [],
  allAlerts: [], // Store all alerts for session saving
  isConnected: false,
  connectionError: null
};

const landmineSlice = createSlice({
  name: 'landmine',
  initialState,
  reducers: {
    addLandmineAlert: (state, action: PayloadAction<LandmineAlert>) => {
      // Add to display alerts (no limit)
      state.alerts = [action.payload, ...state.alerts];
      // Add to accumulated alerts for session saving
      state.allAlerts.push(action.payload);
    },
    removeLandmineAlert: (state, action: PayloadAction<number>) => {
      state.alerts.splice(action.payload, 1);
    },
    clearLandmineAlerts: (state) => {
      state.alerts = [];
      state.allAlerts = [];
    },
    setLandmineConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      if (action.payload) {
        state.connectionError = null;
      }
    },
    setLandmineConnectionError: (state, action: PayloadAction<string | null>) => {
      state.connectionError = action.payload;
    },
    updateLandmineFeedback: (state, action: PayloadAction<{ detected_at: string; feedback: EntryFeedback }>) => {
      const updateIn = (arr: LandmineAlert[]) => {
        const alert = arr.find(a => a.detected_at === action.payload.detected_at);
        if (alert) alert.feedback = action.payload.feedback;
      };
      updateIn(state.alerts);
      updateIn(state.allAlerts);
    }
  }
});

export const {
  addLandmineAlert,
  removeLandmineAlert,
  clearLandmineAlerts,
  setLandmineConnectionStatus,
  setLandmineConnectionError,
  updateLandmineFeedback
} = landmineSlice.actions;

export default landmineSlice.reducer;